import React, { useState, useRef, useEffect } from "react";
import { useTooltip } from "context/TooltipProvider";
import { AssetAppeal, AssetAppealRequestActionStatusEnum, Roles } from "types";
import { hasRole } from "utils/helper";
import { useAuth } from "context/AuthContext";

interface AssetAppealActionsProps {
  action_status?: string;
  disabled?: boolean;
  appeal?: AssetAppeal;
  onAddLowerCourtClick?: React.Dispatch<React.SetStateAction<boolean>>;
  hasLowerCourtData?: boolean;
  hasHigherCourtData?: boolean;
  onAddHigherCourtClick?: React.Dispatch<React.SetStateAction<boolean>>;
  onAddProsecutorClick?: React.Dispatch<React.SetStateAction<boolean>>;
  isAssetCoordinator?: boolean;
  hasProsecutor?: boolean;
}

const AssetAppealActions: React.FC<AssetAppealActionsProps> = ({
  disabled = false,
  hasLowerCourtData,
  action_status,
  appeal,
  onAddLowerCourtClick,
  onAddHigherCourtClick,
  hasHigherCourtData,
  onAddProsecutorClick,
  isAssetCoordinator,
  hasProsecutor,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();

  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleAddLowerCourt = () => {
    onAddLowerCourtClick?.(true);
    setIsOpen(false);
  };
  const handleAddHigherCourt = () => {
    onAddHigherCourtClick?.(true);
    setIsOpen(false);
  };
  const handleAddProsecutor = () => {
    onAddProsecutorClick?.(true);
    setIsOpen(false);
  };
  const handleToggleDropdown = () => {
    if (disabled || !hasAvailableOptions) {
      showTooltip("Actions are currently disabled", "info");
      return;
    }
    setIsOpen(!isOpen);
  };

  const hasAvailableOptions =
    (isRegistrar &&
      // Condition for Lower Court option
      ((appeal?.action_status ===
        AssetAppealRequestActionStatusEnum.APPEAL_APPROVED &&
        !hasLowerCourtData &&
        onAddLowerCourtClick) ||
        // Condition for Higher Court option
        (appeal?.action_status ===
          AssetAppealRequestActionStatusEnum.APPEAL_APPROVED &&
          hasLowerCourtData &&
          !hasHigherCourtData &&
          onAddHigherCourtClick))) ||
    (isAssetCoordinator &&
      appeal?.action_status ===
        AssetAppealRequestActionStatusEnum.APPEAL_SUBMITTED_TO_COURT &&
      hasLowerCourtData &&
      hasHigherCourtData &&
      onAddProsecutorClick &&
      !hasProsecutor);
  // Check if button should be disabled
  const isButtonDisabled = disabled || !hasAvailableOptions;

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={handleToggleDropdown}
        disabled={isButtonDisabled}
        className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-gray-300 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
          isButtonDisabled
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-white text-gray-700 hover:text-gray-900"
        }`}
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
        Actions
        <svg
          className={`w-4 h-4 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && hasAvailableOptions && (
        <div className="absolute right-0 z-10 mt-2 w-60 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1">
            {/* Lower Court Option */}
            {isRegistrar &&
              appeal?.action_status ===
                AssetAppealRequestActionStatusEnum.APPEAL_APPROVED &&
              !hasLowerCourtData &&
              onAddLowerCourtClick && (
                <button
                  className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                  onClick={handleAddLowerCourt}
                >
                  <svg
                    className="w-4 h-4 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="font-medium">Add Lower Court</span>
                </button>
              )}

            {/* Higher Court Option */}
            {isRegistrar &&
              appeal?.action_status ===
                AssetAppealRequestActionStatusEnum.APPEAL_APPROVED &&
              hasLowerCourtData &&
              !hasHigherCourtData &&
              onAddHigherCourtClick && (
                <button
                  className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                  onClick={handleAddHigherCourt}
                >
                  <svg
                    className="w-4 h-4 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                  <span className="font-medium">Add Higher Court</span>
                </button>
              )}
            {isAssetCoordinator &&
              appeal?.action_status ===
                AssetAppealRequestActionStatusEnum.APPEAL_SUBMITTED_TO_COURT &&
              hasLowerCourtData &&
              hasHigherCourtData &&
              onAddProsecutorClick &&
              !hasProsecutor && (
                <button
                  className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                  onClick={handleAddProsecutor}
                >
                  <svg
                    className="w-4 h-4 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="font-medium">Add Prosecutor</span>
                </button>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetAppealActions;
